import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Chip,
  Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type { ChartElement, ChartWaypoint } from '@/types';

const CHART_ELEMENT_TYPES = {
  TITLE: 'title',
  NARRATIVE: 'narrative',
  TABLE: 'table',
  STATIC: 'static',
} as const;

interface ChartElementEditorProps {
  element: ChartElement;
  onElementChange: (element: ChartElement) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isEditable: boolean;
  editMode?: 'full' | 'prompt-only';
  waypoints: ChartWaypoint[];
  isFirst: boolean;
  isLast: boolean;
}

function ChartElementEditor({
  element,
  onElementChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  isEditable,
  editMode = 'full',
  waypoints,
  isFirst,
  isLast,
}: ChartElementEditorProps): React.JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(false);

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onElementChange({ ...element, description: event.target.value });
  };

  const handlePromptChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onElementChange({ ...element, prompt: event.target.value });
  };

  const handleTypeChange = (event: { target: { value: unknown } }): void => {
    const newType = event.target.value as string;
    let newMetadata = element.metadata;

    // Initialize metadata for table type
    if (newType === 'table' && !newMetadata) {
      newMetadata = {
        title: '',
        headers: [],
      };
    }

    onElementChange({ ...element, type: newType, metadata: newMetadata });
  };

  const handleForEachChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onElementChange({ ...element, for_each: event.target.checked });
  };

  const handleWaypointsChange = (
    _event: React.SyntheticEvent,
    newValue: ChartWaypoint[],
  ): void => {
    // newValue will be waypoint objects from the options
    // Store them as objects for now, will convert to entity_ids when saving
    onElementChange({ ...element, waypoints: newValue.map((wp) => wp.entity_id) });
  };

  const handleMetadataChange = (field: string, value: unknown): void => {
    const newMetadata = { ...(element.metadata || {}), [field]: value };
    onElementChange({ ...element, metadata: newMetadata });
  };

  const handleTableHeadersChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const headersString = event.target.value;
    const headers = headersString
      .split(',')
      .map((h) => h.trim())
      .filter((h) => h);
    handleMetadataChange('headers', headers);
  };

  // Normalize element.waypoints - they might be entity_ids or full objects
  // Convert to full objects for display/editing
  const getElementWaypoints = (): ChartWaypoint[] => {
    if (!element.waypoints || element.waypoints.length === 0) return [];
    // If waypoints are entity_ids, find matching waypoint objects
    return (waypoints || []).filter((wp) =>
      element.waypoints.includes(wp.entity_id),
    );
  };

  const waypointOptions = waypoints || [];
  const selectedWaypoints = getElementWaypoints();

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}
        >
          {isEditable && editMode === 'full' && (
            <>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  if (onMoveUp) onMoveUp();
                }}
                disabled={isFirst}
                sx={{ opacity: isFirst ? 0.3 : 1 }}
              >
                <ArrowUpwardIcon fontSize='small' />
              </IconButton>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  if (onMoveDown) onMoveDown();
                }}
                disabled={isLast}
                sx={{ opacity: isLast ? 0.3 : 1 }}
              >
                <ArrowDownwardIcon fontSize='small' />
              </IconButton>
            </>
          )}
          <Typography variant='body2' sx={{ flexGrow: 1, fontStyle: 'italic' }}>
            {element.description || 'No description'}
          </Typography>
          {isEditable && editMode === 'full' && (
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              color='error'
            >
              <DeleteOutlinedIcon />
            </IconButton>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <FormControl
            fullWidth
            size='small'
            disabled={!isEditable || editMode === 'prompt-only'}
          >
            <InputLabel>Element Type</InputLabel>
            <Select
              value={element.type || 'narrative'}
              onChange={handleTypeChange}
              label='Element Type'
            >
              <MenuItem value={CHART_ELEMENT_TYPES.TITLE}>Title</MenuItem>
              <MenuItem value={CHART_ELEMENT_TYPES.NARRATIVE}>
                Narrative
              </MenuItem>
              <MenuItem value={CHART_ELEMENT_TYPES.TABLE}>Table</MenuItem>
              <MenuItem value={CHART_ELEMENT_TYPES.STATIC}>Static</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={element.type === 'static' ? 'Content' : 'Description'}
            value={element.description || ''}
            onChange={handleDescriptionChange}
            disabled={!isEditable || editMode === 'prompt-only'}
            multiline
            rows={3}
            size='small'
            required={
              editMode === 'full' &&
              (element.type === 'narrative' ||
                element.type === 'table' ||
                element.type === 'static')
            }
            error={
              editMode === 'full' &&
              (element.type === 'narrative' ||
                element.type === 'table' ||
                element.type === 'static') &&
              (!element.description || element.description.trim() === '')
            }
            helperText={
              editMode === 'full' &&
              (element.type === 'static'
                ? element.description && element.description.trim() !== ''
                  ? ''
                  : 'Content is required for static elements'
                : element.type === 'narrative' || element.type === 'table'
                  ? element.description && element.description.trim() !== ''
                    ? ''
                    : 'Description is required for narrative and table elements'
                  : '')
            }
          />

          {element.type !== 'static' && (
            <TextField
              fullWidth
              label='Prompt'
              value={element.prompt || ''}
              onChange={handlePromptChange}
              disabled={!isEditable}
              multiline
              rows={3}
              size='small'
              required={
                editMode === 'full' &&
                (element.type === 'narrative' || element.type === 'table')
              }
              error={
                editMode === 'full' &&
                (element.type === 'narrative' || element.type === 'table') &&
                (!element.prompt || element.prompt.trim() === '')
              }
              helperText={
                editMode === 'full' &&
                (element.type === 'narrative' || element.type === 'table')
                  ? element.prompt && element.prompt.trim() !== ''
                    ? 'Instructions for generating this element'
                    : 'Prompt is required for narrative and table elements'
                  : 'Instructions for generating this element'
              }
            />
          )}

          {element.type !== 'static' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={element.for_each || false}
                  onChange={handleForEachChange}
                  disabled={!isEditable || editMode === 'prompt-only'}
                />
              }
              label='For Each'
            />
          )}

          {(element.type === 'narrative' || element.type === 'table') && (
            <Autocomplete
              multiple
              options={waypointOptions}
              getOptionLabel={(option) => option.name || option.entity_id}
              value={selectedWaypoints}
              onChange={handleWaypointsChange}
              isOptionEqualToValue={(option, value) => {
                return option.entity_id === value.entity_id;
              }}
              disabled={!isEditable || editMode === 'prompt-only'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Waypoints'
                  size='small'
                  placeholder='Select waypoints'
                  required={editMode === 'full'}
                  error={
                    editMode === 'full' &&
                    (!selectedWaypoints || selectedWaypoints.length === 0)
                  }
                  helperText={
                    editMode === 'full' &&
                    (!selectedWaypoints || selectedWaypoints.length === 0) &&
                    (element.type === 'narrative' || element.type === 'table')
                      ? 'At least one waypoint is required for narrative and table elements'
                      : ''
                  }
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const label = option.name || option.entity_id;
                  return (
                    <Chip
                      variant='outlined'
                      label={label}
                      {...getTagProps({ index })}
                      key={option.entity_id}
                    />
                  );
                })
              }
            />
          )}

          {element.type === 'table' && (
            <Box>
              <TextField
                fullWidth
                label='Table Title'
                value={(element.metadata?.['title'] as string) || ''}
                onChange={(e) => handleMetadataChange('title', e.target.value)}
                disabled={!isEditable || editMode === 'prompt-only'}
                size='small'
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                label='Table Headers (comma-separated)'
                value={((element.metadata?.['headers'] as string[]) || []).join(', ')}
                onChange={handleTableHeadersChange}
                disabled={!isEditable || editMode === 'prompt-only'}
                size='small'
                helperText='Enter column headers separated by commas, e.g., "ID, Name, Description"'
              />
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default ChartElementEditor;

